import React, { useEffect, useRef, useState } from 'react'

export default function ScrollReveal({
  children,
  className = '',
  variant = 'up', // 'up' | 'left' | 'right' | 'scale' | 'fade'
  delay = 0,
  duration = 700,
  threshold = 0.15,
  once = true
}) {
  const [isVisible, setIsVisible] = useState(false)
  const domRef = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true)
            if (once && domRef.current) {
              observer.unobserve(domRef.current)
            }
          } else if (!once) {
            setIsVisible(false)
          }
        })
      },
      {
        threshold,
        rootMargin: '0px 0px -50px 0px'
      }
    )

    const currentRef = domRef.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [threshold, once])

  const getVariantStyles = () => {
    if (isVisible) {
      return {
        opacity: 1,
        transform: 'translate3d(0, 0, 0) scale(1)',
        transition: `opacity ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`
      }
    }

    let initialTransform = 'translate3d(0, 30px, 0)'
    if (variant === 'left') initialTransform = 'translate3d(-35px, 0, 0)'
    if (variant === 'right') initialTransform = 'translate3d(35px, 0, 0)'
    if (variant === 'scale') initialTransform = 'translate3d(0, 20px, 0) scale(0.95)'
    if (variant === 'fade') initialTransform = 'translate3d(0, 0, 0)'

    return {
      opacity: 0,
      transform: initialTransform,
      transition: `opacity ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
      willChange: 'opacity, transform'
    }
  }

  return (
    <div ref={domRef} className={className} style={getVariantStyles()}>
      {children}
    </div>
  )
}
